import { useNavigate, useParams } from '@tanstack/react-router';
import { Ban, CheckCircle2, Copy, Mail, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { DraftOrder, DraftOrderBlocker } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@nucleus-commerce/ui/components/tooltip';

import { OrderLink } from '@/features/orders';
import { useStore } from '@/shared/store/store-context';

import { BlockerList } from '../components/blocker-list';
import { CompleteDialog } from '../components/complete-dialog';
import { ConfirmDraftDialog, type DraftConfirm } from '../components/confirm-draft-dialog';
import { DraftAddressesCard } from '../components/draft-addresses-card';
import { DraftCustomerCard } from '../components/draft-customer-card';
import { DraftDiscountCard } from '../components/draft-discount-card';
import { DraftLinesCard } from '../components/draft-lines-card';
import { DraftNotesCard } from '../components/draft-notes-card';
import { DraftOrderStatusBadge } from '../components/draft-order-status-badge';
import { DraftShippingCard } from '../components/draft-shipping-card';
import { DraftTotalsCard } from '../components/draft-totals-card';
import { SendInvoiceDialog } from '../components/send-invoice-dialog';
import { useDraftOrder } from '../hooks/use-draft-order';
import { useDuplicateDraftOrder } from '../hooks/use-draft-order-mutations';
import {
  canCancel,
  canDelete,
  invoiceGateBlockers,
  isEditable,
} from '../schemas/draft-order-rules';

const ROUTE = '/_authed/_active/s/$storeId/draft-orders/$draftId' as const;

/** Disabled with the blockers listed in a tooltip, so staff see why without clicking. */
function GuardedButton({
  blockers,
  icon,
  label,
  variant = 'outline',
  tour,
  onClick,
}: {
  blockers: DraftOrderBlocker[];
  icon: ReactNode;
  label: string;
  variant?: 'outline' | 'default';
  tour: string;
  onClick: () => void;
}) {
  const { t } = useTranslation('draftOrders');
  const blocked = blockers.length > 0;
  const button = (
    <Button variant={variant} disabled={blocked} onClick={onClick} data-tour={tour}>
      {icon}
      {label}
    </Button>
  );
  if (!blocked) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{button}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="mb-1 font-medium">{t('actions.blockedBy')}</p>
        <BlockerList blockers={blockers} />
      </TooltipContent>
    </Tooltip>
  );
}

function Description({ draft }: { draft: DraftOrder }) {
  const { t } = useTranslation('draftOrders');
  const format = useFormat();
  const store = useStore();
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span title={format.utc(draft.createdAt)}>
        {t('detail.created', { when: format.relative(draft.createdAt) })}
      </span>
      {draft.invoiceSentAt ? (
        <>
          <span aria-hidden>·</span>
          <span title={format.utc(draft.invoiceSentAt)}>
            {t('detail.invoiceSent', { when: format.relative(draft.invoiceSentAt) })}
          </span>
        </>
      ) : null}
      {draft.completedAt && draft.orderId ? (
        <>
          <span aria-hidden>·</span>
          <span title={format.utc(draft.completedAt)}>
            {t('detail.completed', { when: format.relative(draft.completedAt) })}
          </span>
          <OrderLink storeId={store.id} orderId={draft.orderId} name={draft.orderName} />
        </>
      ) : null}
      {draft.cancelledAt ? (
        <>
          <span aria-hidden>·</span>
          <span title={format.utc(draft.cancelledAt)}>
            {t('detail.cancelled', { when: format.relative(draft.cancelledAt) })}
          </span>
        </>
      ) : null}
    </span>
  );
}

type DraftDialog = 'invoice' | 'complete';

function DraftEditor({ draft }: { draft: DraftOrder }) {
  const { t } = useTranslation('draftOrders');
  const store = useStore();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const duplicate = useDuplicateDraftOrder(store.id);
  const [dialog, setDialog] = useState<DraftDialog | null>(null);
  const [confirm, setConfirm] = useState<DraftConfirm | null>(null);

  const writable = can('orders.write');
  const editable = writable && isEditable(draft.status);

  const copy = () =>
    duplicate.mutate(draft.id, {
      onSuccess: (copied) =>
        void navigate({
          to: '/s/$storeId/draft-orders/$draftId',
          params: { storeId: store.id, draftId: copied.id },
        }),
    });

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-mono">{draft.name}</span>}
        meta={<DraftOrderStatusBadge status={draft.status} />}
        description={<Description draft={draft} />}
        backTo={{ to: '/s/$storeId/draft-orders', params: { storeId: store.id } }}
        backLabel={t('backToList')}
        actions={
          writable ? (
            <>
              <Button variant="outline" disabled={duplicate.isPending} onClick={copy}>
                <Copy aria-hidden />
                {duplicate.isPending ? t('actions.duplicating') : t('actions.duplicate')}
              </Button>
              {editable && canDelete(draft.status) ? (
                <Button variant="outline" onClick={() => setConfirm('delete')}>
                  <Trash2 aria-hidden />
                  {t('actions.delete')}
                </Button>
              ) : null}
              {editable && canCancel(draft.status) ? (
                <Button variant="outline" onClick={() => setConfirm('cancel')}>
                  <Ban aria-hidden />
                  {t('actions.cancel')}
                </Button>
              ) : null}
              {editable ? (
                <>
                  <GuardedButton
                    blockers={invoiceGateBlockers(draft)}
                    tour="draft-orders.detail.send-invoice"
                    icon={<Mail aria-hidden />}
                    label={
                      draft.status === 'invoice_sent'
                        ? t('actions.resendInvoice')
                        : t('actions.sendInvoice')
                    }
                    onClick={() => setDialog('invoice')}
                  />
                  <GuardedButton
                    blockers={draft.blockers}
                    tour="draft-orders.detail.complete"
                    icon={<CheckCircle2 aria-hidden />}
                    label={t('actions.complete')}
                    variant="default"
                    onClick={() => setDialog('complete')}
                  />
                </>
              ) : null}
            </>
          ) : null
        }
      />
      {!isEditable(draft.status) ? (
        <Alert role="status">
          <AlertDescription className="flex flex-wrap items-center gap-1">
            {draft.status === 'completed'
              ? t('detail.readOnlyCompleted')
              : t('detail.readOnlyCancelled')}
            {draft.orderId ? (
              <OrderLink
                storeId={store.id}
                orderId={draft.orderId}
                name={t('detail.viewOrder', { name: draft.orderName ?? '' })}
                className="font-sans font-medium underline"
              />
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      <FormLayout
        aside={
          <>
            <DraftCustomerCard
              key={`${draft.customerId}|${draft.email}|${draft.phone}`}
              storeId={store.id}
              draft={draft}
              editable={editable}
            />
            <DraftAddressesCard storeId={store.id} draft={draft} editable={editable} />
            <DraftNotesCard
              key={`${draft.note}|${draft.staffNotes}|${draft.tags.join(',')}`}
              storeId={store.id}
              draft={draft}
              editable={editable}
            />
          </>
        }
      >
        <DraftLinesCard storeId={store.id} draft={draft} editable={editable} />
        <div className="grid gap-6 md:grid-cols-2">
          <DraftShippingCard
            key={JSON.stringify([draft.shipping, draft.availableRates.map((r) => r.id)])}
            storeId={store.id}
            draft={draft}
            editable={editable}
          />
          <DraftDiscountCard
            key={JSON.stringify(draft.discount && { ...draft.discount, amount: null })}
            storeId={store.id}
            draft={draft}
            editable={editable}
          />
        </div>
        <DraftTotalsCard draft={draft} />
      </FormLayout>
      {dialog === 'invoice' ? (
        <SendInvoiceDialog
          storeId={store.id}
          draft={draft}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'complete' ? (
        <CompleteDialog
          storeId={store.id}
          draft={draft}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      <ConfirmDraftDialog
        storeId={store.id}
        draft={draft}
        action={confirm}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
      />
    </PageContent>
  );
}

function DraftOrderLoader() {
  const store = useStore();
  const { draftId } = useParams({ from: ROUTE });
  const { data: draft } = useDraftOrder(store.id, draftId);
  return draft ? <DraftEditor draft={draft} /> : null;
}

export function DraftOrderPage() {
  return (
    <FeatureErrorBoundary name="draftOrders.detail">
      <DraftOrderLoader />
    </FeatureErrorBoundary>
  );
}
